import { ChatMessage, ChatPlatform } from 'lib/twitchChatParse'

const INVISIBLE = /\p{Cf}|\p{Variation_Selector}|\u034F|\u17B4|\u17B5|[\u115F\u1160\u3164\uFFA0]/gu
const DIACRITICS = /\p{Script=Inherited}/gu
const WHITESPACE_RUN = /\s+/g
const SPACED_RUN = /(?<![\p{L}\p{N}])\p{L}(?:[ .]\p{L})+(?![\p{L}\p{N}])/gu
const RUN_SEPARATORS = /[ .]/g

export const normalizeForBlocking = (s: string): string =>
  s.normalize('NFKC')
    .replace(INVISIBLE, '')
    .toLowerCase()
    .normalize('NFD')
    .replace(DIACRITICS, '')
    .normalize('NFC')
    .replace(WHITESPACE_RUN, ' ')

export const collapseSpacedLetters = (s: string): string =>
  s.replace(SPACED_RUN, (run) => run.replace(RUN_SEPARATORS, ''))

export const dotSpacedLetters = (s: string): string =>
  s.replace(SPACED_RUN, (run) => run.replace(RUN_SEPARATORS, '.'))

const SEPARATED_RUN = /(?<![\p{L}\p{M}\p{N}])\p{L}(?:[^\p{L}\p{M}\p{N}]+\p{L})+(?![\p{L}\p{M}\p{N}])/gu
const RUN_SEPARATOR_RUNS = /[^\p{L}\p{M}\p{N}]+/gu

export const collapseSeparatedLetters = (s: string): string =>
  s.replace(SEPARATED_RUN, (run) => run.replace(RUN_SEPARATOR_RUNS, ''))

export const dotSeparatedLetters = (s: string): string =>
  s.replace(SEPARATED_RUN, (run) => run.replace(RUN_SEPARATOR_RUNS, '.'))

const REPEATED_LETTER = /(\p{L})\1/u
const LETTER_OF_WORD = /\p{L}/u

export interface RepeatPattern {
  test: (s: string) => boolean
}

interface RepeatToken {
  readonly ch: string
  readonly alt: string | null
  readonly min: number
  readonly open: boolean
}

const stepsMeet = (a: RepeatToken, b: RepeatToken): boolean =>
  a.open && b.open && (a.ch === b.ch || a.ch === b.alt || (a.alt != null && (a.alt === b.ch || a.alt === b.alt)))

const repeatScanOf = (tokens: readonly RepeatToken[]): RepeatPattern => {
  const base: number[] = []
  let size = 0
  for (const tok of tokens) {
    base.push(size)
    size += tok.min + 1
  }
  const last = tokens.length
  return Object.freeze({
    test: (s: string): boolean => {
      let cur = new Uint8Array(size)
      let next = new Uint8Array(size)
      for (const x of s) {
        cur[0] = 1
        next.fill(0)
        for (let t = 0; t < last; t += 1) {
          const tok = tokens[t]
          if (x !== tok.ch && x !== tok.alt) continue
          for (let c = 0; c <= tok.min; c += 1) {
            if (cur[base[t] + c] !== 1) continue
            if (tok.open) next[base[t] + Math.min(c + 1, tok.min)] = 1
            else if (c === 0) next[base[t] + 1] = 1
          }
        }
        for (let t = 0; t < last; t += 1) {
          if (next[base[t] + tokens[t].min] !== 1) continue
          if (t + 1 === last) return true
          next[base[t + 1]] = 1
        }
        const done = cur
        cur = next
        next = done
      }
      return false
    }
  })
}

const repeatPatternOf = (w: string, stroke: boolean): RepeatPattern | null => {
  const cs = Array.from(w)
  let out = ''
  let letters = 0
  const tokens: RepeatToken[] = []
  let i = 0
  while (i < cs.length) {
    const ch = cs[i]
    let n = 1
    while (i + n < cs.length && cs[i + n] === ch) n += 1
    if (LETTER_OF_WORD.test(ch)) {
      letters += 1
      let cls = ch.replace(REGEX_SYNTAX, '\\$&')
      if (stroke && ch === 'i') cls = `[i${STROKE}]`
      if (stroke && ch === 'l') cls = `[l${STROKE}]`
      out += n === 1 ? `${cls}+` : `${cls}{${n},}`
      tokens.push({ ch, alt: stroke && (ch === 'i' || ch === 'l') ? STROKE : null, min: n, open: true })
    } else {
      out += ch.replace(REGEX_SYNTAX, '\\$&').repeat(n)
      for (let k = 0; k < n; k += 1) tokens.push({ ch, alt: null, min: 1, open: false })
    }
    i += n
  }
  if (letters === 0) return null
  const splits = tokens.some((tok, k) => k > 0 && stepsMeet(tokens[k - 1], tok))
  return splits ? repeatScanOf(tokens) : new RegExp(out, 'u')
}

const pairs = (from: readonly number[], to: string): Array<[string, string]> =>
  from.map((cp, i) => [String.fromCodePoint(cp), to.charAt(i)])

export const LOOKALIKE_LETTERS: ReadonlyMap<string, string> = new Map([
  ...pairs([0x0405, 0x0408, 0x0410, 0x0412, 0x0415, 0x041A, 0x041C, 0x041D, 0x041E, 0x0420, 0x0421, 0x0422, 0x0423, 0x0425, 0x042C, 0x0474, 0x04AE, 0x04BA, 0x050C, 0x051A, 0x051C], 'sjabekmhopctyxbvyhgqw'),
  ...pairs([0x0430, 0x0432, 0x0433, 0x0435, 0x043A, 0x043D, 0x043E, 0x0440, 0x0441, 0x0442, 0x0443, 0x0445, 0x0448, 0x0455, 0x0456, 0x0458, 0x0461, 0x0475, 0x04AF, 0x04BB, 0x04BD, 0x0501, 0x050D, 0x051B, 0x051D, 0x1C82, 0x1C83, 0xA647, 0xA68D], 'abrekhopctyxwsijwvyhedgqwocit'),
  ...pairs([0x037F, 0x0391, 0x0392, 0x0395, 0x0396, 0x0397, 0x039A, 0x039C, 0x039D, 0x039F, 0x03A1, 0x03A4, 0x03A5, 0x03A7, 0x03D2, 0x03DC, 0x03F9, 0x03FA], 'jabezhkmnoptyxyfcm'),
  ...pairs([0x03B1, 0x03B3, 0x03B9, 0x03BA, 0x03BD, 0x03BF, 0x03C1, 0x03C3, 0x03C4, 0x03C5, 0x03ED, 0x03F0, 0x03F1, 0x03F2, 0x03F3, 0x03F8], 'ayikvopotuokpcjp'),
  ...pairs([0x054D, 0x054F, 0x0555, 0x0561, 0x0563, 0x0566, 0x0570, 0x0575, 0x0578, 0x057C, 0x057D, 0x0581, 0x0582, 0x0584, 0x0585], 'usowqqhjnnugifo'),
  ...pairs([0x13A0, 0x13A1, 0x13A2, 0x13A5, 0x13A9, 0x13AA, 0x13AB, 0x13AC, 0x13B3, 0x13B7, 0x13BB, 0x13BD, 0x13C0, 0x13C2, 0x13C3, 0x13CF, 0x13D2, 0x13D4, 0x13D5, 0x13D9, 0x13DA, 0x13DE, 0x13DF, 0x13E2, 0x13E6, 0x13E7, 0x13F3, 0x13F4], 'drtiyajewmhyghzbrwsvslcpkdgb'),
  ...pairs([0x13FB, 0x13FC, 0xAB70, 0xAB71, 0xAB72, 0xAB75, 0xAB7A, 0xAB7B, 0xAB7C, 0xAB81, 0xAB83, 0xAB8B, 0xAB90, 0xAB93, 0xABA2, 0xABA4, 0xABA9, 0xABAA, 0xABAE, 0xABAF, 0xABB2, 0xABB6], 'gbdrtiajerwhgzrwvslcpk'),
  ...pairs([0xA4D0, 0xA4D1, 0xA4D2, 0xA4D3, 0xA4D4, 0xA4D6, 0xA4D7, 0xA4D9, 0xA4DA, 0xA4DC, 0xA4DD, 0xA4DF, 0xA4E0, 0xA4E1, 0xA4E2, 0xA4E3, 0xA4E6, 0xA4E7, 0xA4EA, 0xA4EB, 0xA4EC, 0xA4EE, 0xA4F0, 0xA4F3, 0xA4F4], 'bpddtgkjczfmnlsrvhwxyaeou'),
  ...pairs([0x0184, 0x018D, 0x0192, 0x01A6, 0x01BD, 0x01BF, 0x0237, 0x024C, 0x0251, 0x0261, 0x0263, 0x0269, 0x026F, 0x0284, 0x028B, 0x1D11, 0x1D26, 0xA798, 0xA799, 0xA79F, 0xA7B2, 0xA7B3, 0xA7B4, 0xA7FA, 0xAB32, 0xAB35, 0xAB3D, 0xAB47, 0xAB48, 0xAB4E, 0xAB52, 0xAB5A, 0xAB64], 'bgfrspjragyiwfuorffujxbweforruuya'),
  ...pairs([0x0131, 0x00FE, 0x2C6B, 0x2C6C, 0x0138], 'ipzzk'),
  ...pairs([0x1D00, 0x0299, 0x1D04, 0x1D05, 0x1D07, 0xA730, 0x0262, 0x029C, 0x026A, 0x1D0A, 0x1D0B, 0x029F, 0x1D0D, 0x0274, 0x1D0F, 0x1D18, 0xA7AF, 0x0280, 0xA731, 0x1D1B, 0x1D1C, 0x1D20, 0x1D21, 0x028F, 0x1D22], 'abcdefghijklmnopqrstuvwyz')
])

const VERTICAL_STROKES: ReadonlySet<string> = new Set(
  [0x0049, 0x0399, 0x0406, 0x04C0, 0x04CF, 0xA4F2, 0x0196, 0xA7AE, 0x01C0, 0xA7FE, 0xA781].map((cp) => String.fromCodePoint(cp))
)

export const STROKE = '\u01C2'

const ENCLOSED_LETTER_RANGES: ReadonlyArray<readonly [number, number]> = [[0x1F150, 0x1F169], [0x1F170, 0x1F189], [0x1F1E6, 0x1F1FF]]

const lookalikeOf = (c: string, stroke: string): string => {
  if (VERTICAL_STROKES.has(c)) return stroke
  const latin = LOOKALIKE_LETTERS.get(c)
  if (latin != null) return latin
  const cp = c.codePointAt(0) ?? 0
  const range = ENCLOSED_LETTER_RANGES.find(([first, last]) => cp >= first && cp <= last)
  return range == null ? c : String.fromCharCode(0x61 + cp - range[0])
}

const foldLetters = (s: string, stroke: string): string => {
  const once = Array.from(s, (c) => lookalikeOf(c, stroke)).join('')
  const stripped = once.normalize('NFKC').replace(INVISIBLE, '').normalize('NFD').replace(DIACRITICS, '').normalize('NFC')
  return normalizeForBlocking(Array.from(stripped, (c) => lookalikeOf(c, stroke)).join(''))
}

const DIGIT_LOOKALIKES: ReadonlyMap<string, string> = new Map([...'034578@$'].map((c, i) => [c, 'oeastbas'.charAt(i)]))
const digitLookalikeOf = (c: string | undefined): string | undefined =>
  c === '1' || c === '|' || c === '!' ? STROKE : (c == null ? undefined : DIGIT_LOOKALIKES.get(c))
const LETTER = /\p{L}/u
const isLetter = (c: string | undefined): boolean => c != null && LETTER.test(c)
const WORD_RUN = /\S+/g

const swapInnerDigits = (s: string): string => s.replace(WORD_RUN, (word) => {
  const cs = Array.from(word)
  const out = [...cs]
  let i = 0
  while (i < cs.length) {
    if (digitLookalikeOf(cs[i]) == null) {
      i += 1
      continue
    }
    let end = i
    while (end < cs.length && digitLookalikeOf(cs[end]) != null) end += 1
    if (isLetter(cs[i - 1]) && isLetter(cs[end])) {
      for (let k = i; k < end; k += 1) out[k] = digitLookalikeOf(cs[k]) ?? cs[k]
    }
    i = end
  }
  return out.join('')
})

export const foldLookalikes = (s: string): string => swapInnerDigits(foldLetters(s, STROKE))

export const foldSavedWord = (s: string): string => foldLetters(s, 'i')

const REGEX_SYNTAX = /[\\^$.*+?()[\]{}|/]/g
const strokePattern = (w: string): RegExp => new RegExp(
  Array.from(w, (c) => {
    if (c === 'i') return `[i${STROKE}]`
    if (c === 'l') return `[l${STROKE}]`
    return c.replace(REGEX_SYNTAX, '\\$&')
  }).join(''),
  'u'
)

export interface LookalikeWords {
  readonly words: readonly string[]
  readonly dotted: readonly string[]
  readonly wordPatterns: readonly RegExp[]
  readonly dottedPatterns: readonly RegExp[]
  readonly sameAsSaved: boolean
  readonly forms: ExtraForms
}

export interface ExtraForms {
  readonly separated: readonly string[]
  readonly joinable: readonly boolean[]
  readonly separatedPatterns: readonly RegExp[] | null
  readonly repeats: ReadonlyArray<RepeatPattern | null>
}

const EMPTY_EXTRA_FORMS: ExtraForms = Object.freeze({
  separated: Object.freeze([]),
  joinable: Object.freeze([]),
  separatedPatterns: null,
  repeats: Object.freeze([])
})

const extraFormsOf = (words: readonly string[], stroke: boolean): ExtraForms => {
  const separated = words.map(dotSeparatedLetters)
  return Object.freeze({
    separated: Object.freeze(separated),
    joinable: Object.freeze(words.map((w) => collapseSeparatedLetters(w) === w)),
    separatedPatterns: stroke ? Object.freeze(separated.map(strokePattern)) : null,
    repeats: Object.freeze(words.map((w) => repeatPatternOf(w, stroke)))
  })
}

const EMPTY_LOOKALIKE_WORDS: LookalikeWords = Object.freeze({
  words: Object.freeze([]),
  dotted: Object.freeze([]),
  wordPatterns: Object.freeze([]),
  dottedPatterns: Object.freeze([]),
  sameAsSaved: true,
  forms: EMPTY_EXTRA_FORMS
})

export interface BlockedMatcher {
  readonly words: readonly string[]
  readonly dotted: readonly string[]
  readonly lookalike: LookalikeWords
  readonly forms: ExtraForms
  readonly key: string
}

export const EMPTY_BLOCKED_MATCHER: BlockedMatcher = Object.freeze({ words: Object.freeze([]), dotted: Object.freeze([]), lookalike: EMPTY_LOOKALIKE_WORDS, forms: EMPTY_EXTRA_FORMS, key: '[]' })

export const compileBlockedWords = (raw: readonly unknown[] | null | undefined): BlockedMatcher => {
  if (raw == null || raw.length === 0) return EMPTY_BLOCKED_MATCHER
  const words = [...new Set(raw
    .filter((w): w is string => typeof w === 'string')
    .map((w) => normalizeForBlocking(w).trim())
    .filter((w) => w !== ''))]
  if (words.length === 0) return EMPTY_BLOCKED_MATCHER
  const readings = [...new Set(raw
    .filter((w): w is string => typeof w === 'string')
    .map((w) => foldSavedWord(w).trim())
    .filter((w) => w !== ''))]
  const readingsDotted = readings.map(dotSpacedLetters)
  const lookalike: LookalikeWords = Object.freeze({
    words: Object.freeze(readings),
    dotted: Object.freeze(readingsDotted),
    wordPatterns: Object.freeze(readings.map(strokePattern)),
    dottedPatterns: Object.freeze(readingsDotted.map(strokePattern)),
    sameAsSaved: readings.length === words.length && readings.every((w, i) => w === words[i]),
    forms: extraFormsOf(readings, true)
  })
  return Object.freeze({
    words: Object.freeze(words),
    dotted: Object.freeze(words.map(dotSpacedLetters)),
    lookalike,
    forms: extraFormsOf(words, false),
    key: lookalike.sameAsSaved ? JSON.stringify(words) : JSON.stringify([words, readings])
  })
}

const matcherCache = new WeakMap<readonly unknown[], BlockedMatcher>()
export const matcherFor = (raw: readonly unknown[]): BlockedMatcher => {
  const cached = matcherCache.get(raw)
  if (cached != null) return cached
  const made = compileBlockedWords(raw)
  matcherCache.set(raw, made)
  return made
}

const extraIsBlocked = (
  words: readonly string[],
  forms: ExtraForms,
  wordPatterns: readonly RegExp[] | null,
  reading: string
): boolean => {
  if (words.length === 0) return false
  const stroke = forms.separatedPatterns != null && wordPatterns != null && reading.includes(STROKE)
  const joined = collapseSeparatedLetters(reading)
  const separated = dotSeparatedLetters(reading)
  const has = (text: string, word: string, pattern: RegExp | undefined): boolean =>
    stroke && pattern != null ? pattern.test(text) : text.includes(word)
  if (joined !== reading || separated !== reading) {
    const hit = words.some((w, i) =>
      has(separated, forms.separated[i], forms.separatedPatterns?.[i]) ||
      (forms.joinable[i] && has(joined, w, wordPatterns?.[i])))
    if (hit) return true
  }
  const narrow = collapseSpacedLetters(reading)
  const anyRepeat = REPEATED_LETTER.test(reading) || REPEATED_LETTER.test(narrow) || REPEATED_LETTER.test(joined)
  if (!anyRepeat) return false
  return words.some((_w, i) => {
    const p = forms.repeats[i]
    if (p == null) return false
    return p.test(reading) || (forms.joinable[i] && (p.test(narrow) || p.test(joined)))
  })
}

const readingIsBlocked = (lw: LookalikeWords, folded: string, plain: string): boolean => {
  if (folded === plain && lw.sameAsSaved) return false
  const joined = collapseSpacedLetters(folded)
  const dotted = dotSpacedLetters(folded)
  const hasStroke = folded.includes(STROKE)
  const today = lw.words.some((w, i) => {
    const d = lw.dotted[i]
    if (!hasStroke) return folded.includes(w) || dotted.includes(d) || (w === d && joined.includes(w))
    const wp = lw.wordPatterns[i]
    return wp.test(folded) || lw.dottedPatterns[i].test(dotted) || (w === d && wp.test(joined))
  })
  return today || extraIsBlocked(lw.words, lw.forms, lw.wordPatterns, folded)
}

const lookalikeIsBlocked = (lw: LookalikeWords, text: string, plain: string): boolean => {
  if (lw.words.length === 0) return false
  const letters = foldLetters(text, STROKE)
  const folded = swapInnerDigits(letters)
  return readingIsBlocked(lw, folded, plain) || (folded !== letters && readingIsBlocked(lw, letters, plain))
}

export const textIsBlocked = (matcher: BlockedMatcher, text: string): boolean => {
  if (matcher.words.length === 0 || typeof text !== 'string' || text === '') return false
  const plain = normalizeForBlocking(text)
  const joined = collapseSpacedLetters(plain)
  const dotted = dotSpacedLetters(plain)
  const today = matcher.words.some((w, i) => {
    const d = matcher.dotted[i]
    return plain.includes(w) || dotted.includes(d) || (w === d && joined.includes(w))
  })
  return today ||
    extraIsBlocked(matcher.words, matcher.forms, null, plain) ||
    lookalikeIsBlocked(matcher.lookalike, text, plain)
}

export const lineIsBlocked = (matcher: BlockedMatcher, text: string, displayName: string): boolean =>
  textIsBlocked(matcher, text) || textIsBlocked(matcher, displayName)

export const messageIsBlocked = (matcher: BlockedMatcher, message: Pick<ChatMessage, 'text' | 'displayName'>): boolean =>
  lineIsBlocked(matcher, message.text, message.displayName)

export type MessageHit = (m: ChatMessage) => boolean

export const blockedHitOf = (matcher: BlockedMatcher): MessageHit | null =>
  matcher.words.length === 0 ? null : (m) => messageIsBlocked(matcher, m)

export const twitchDeleteHit = (targetMsgId: string): MessageHit =>
  (m) => m.id === targetMsgId

export const twitchClearHit = (channel: string, targetLogin: string | null): MessageHit =>
  (m) => m.channel === channel && (targetLogin === null || m.login === targetLogin)

export const youtubeBanHitOf = (envelope: { authorChannelId?: unknown, bannedAtMs?: unknown }): MessageHit | null => {
  const authorChannelId = envelope.authorChannelId
  if (typeof authorChannelId !== 'string' || authorChannelId === '') return null
  const bannedAtMs = envelope.bannedAtMs
  if (typeof bannedAtMs !== 'number' || !Number.isFinite(bannedAtMs) || bannedAtMs <= 0) {
    return (m) => m.platform === 'YOUTUBE' && m.login === authorChannelId
  }
  return (m) => m.platform === 'YOUTUBE' && m.login === authorChannelId && !(m.publishedAt > bannedAtMs)
}

export const chatRemoveHitOf = (platform: ChatPlatform, id: string): MessageHit =>
  (m) => m.platform === platform && m.id === id

export const youtubeDeleteHitOf = (ids: readonly unknown[]): MessageHit | null => {
  const set = new Set(ids.filter((id): id is string => typeof id === 'string' && id !== ''))
  if (set.size === 0) return null
  return (m) => m.platform === 'YOUTUBE' && set.has(m.id)
}

export interface ChatPurgeSurfaces<P extends { message: ChatMessage }> {
  pendingRef: { current: P[] }
  setMessages: (update: (prev: ChatMessage[]) => ChatMessage[]) => void
  feedRef: { current: { removeReal: (key: string, hit: MessageHit) => void, blockIds?: (key: string, ids: readonly string[]) => void } | null }
  feedKeyRef: { current: string }
}

export const purgeChatSurfaces = <P extends { message: ChatMessage }>(s: ChatPurgeSurfaces<P>, hit: MessageHit, ids?: readonly string[]): void => {
  s.pendingRef.current = s.pendingRef.current.filter((e) => !hit(e.message))
  s.setMessages((prev) => {
    const kept = prev.filter((m) => !hit(m))
    return kept.length === prev.length ? prev : kept
  })
  const feed = s.feedRef.current
  if (feed != null) {
    feed.removeReal(s.feedKeyRef.current, hit)
    if (ids != null && ids.length > 0 && feed.blockIds != null) feed.blockIds(s.feedKeyRef.current, ids)
  }
}
